/**
 * Utilities per ottimizzazioni React e performance
 * Centralizza hook e funzioni per migliorare performance dell'app
 */

import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';

/**
 * Hook per debouncing di valori (ricerche, filtri, ecc.)
 * @param {any} value - Valore da debounceare
 * @param {number} delay - Ritardo in millisecondi
 * @returns {any} - Valore debouncato
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook per throttling di funzioni (scroll, resize, ecc.)
 * @param {Function} callback - Funzione da throttle
 * @param {number} delay - Ritardo in millisecondi
 * @returns {Function} - Funzione throttled
 */
export const useThrottle = (callback, delay) => {
  const [ready, setReady] = useState(true);

  const throttledCallback = useCallback((...args) => {
    if (ready) {
      callback(...args);
      setReady(false);
      setTimeout(() => {
        setReady(true);
      }, delay);
    }
  }, [ready, callback, delay]);

  return throttledCallback;
};

/**
 * Hook per lazy loading di dati con cache
 * @param {Function} fetchFunction - Funzione per caricare i dati
 * @param {Array} dependencies - Dipendenze per ricaricamento
 * @param {Object} options - Opzioni (cache, retry, ecc.)
 * @returns {Object} - { data, loading, error, refetch, refresh }
 */
export const useLazyData = (fetchFunction, dependencies = [], options = {}) => {
  const {
    enableCache = true,
    cacheKey = null,
    retryCount = 3,
    retryDelay = 1000
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const retryCountRef = useRef(0);
  const cacheRef = useRef(new Map());

  const loadData = useCallback(async (force = false) => {
    const key = cacheKey || JSON.stringify(dependencies);
    
    // Verifica cache se abilitata
    if (enableCache && !force && cacheRef.current.has(key)) {
      const cachedData = cacheRef.current.get(key);
      setData(cachedData);
      return cachedData;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFunction();
      
      // Salva in cache
      if (enableCache) {
        cacheRef.current.set(key, result);
      }
      
      setData(result);
      retryCountRef.current = 0;
      return result;
    } catch (err) {
      setError(err);
      
      // Retry automatico
      if (retryCountRef.current < retryCount) {
        retryCountRef.current++;
        setTimeout(() => {
          loadData(force);
        }, retryDelay * retryCountRef.current);
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, enableCache, cacheKey, retryCount, retryDelay, ...dependencies]);

  const refresh = useCallback(() => {
    return loadData(true);
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    data,
    loading,
    error,
    refetch: loadData,
    refresh
  };
};

/**
 * Hook per virtualizzazione di liste grandi
 * @param {Array} items - Array di elementi
 * @param {number} itemHeight - Altezza di ogni elemento
 * @param {number} containerHeight - Altezza del container
 * @param {number} overscan - Elementi extra da renderizzare
 * @returns {Object} - { visibleItems, startIndex, endIndex, totalHeight, scrollToIndex }
 */
export const useVirtualList = (items, itemHeight, containerHeight, overscan = 5) => {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleCount = Math.ceil(containerHeight / itemHeight);
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(items.length - 1, startIndex + visibleCount + overscan * 2);
  
  const visibleItems = useMemo(() => {
    return items.slice(startIndex, endIndex + 1).map((item, index) => ({
      ...item,
      index: startIndex + index,
      top: (startIndex + index) * itemHeight
    }));
  }, [items, startIndex, endIndex, itemHeight]);

  const totalHeight = items.length * itemHeight;

  const scrollToIndex = useCallback((index) => {
    const scrollTo = index * itemHeight;
    setScrollTop(scrollTo);
  }, [itemHeight]);

  const handleScroll = useCallback((event) => {
    setScrollTop(event.target.scrollTop);
  }, []);

  return {
    visibleItems,
    startIndex,
    endIndex,
    totalHeight,
    scrollToIndex,
    handleScroll
  };
};

/**
 * Hook per gestione stato con ottimizzazioni
 * @param {any} initialState - Stato iniziale
 * @returns {Object} - { state, setState, resetState, updateField }
 */
export const useOptimizedState = (initialState) => {
  const [state, setState] = useState(initialState);

  const updateField = useCallback((field, value) => {
    setState(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, [initialState]);

  const memoizedState = useMemo(() => state, [state]);

  return {
    state: memoizedState,
    setState,
    resetState,
    updateField
  };
};

/**
 * Hook per preload di risorse
 * @param {Array} urls - Array di URL da preloadare
 * @param {string} type - Tipo di risorsa ('image', 'script', 'style')
 * @returns {Object} - { loaded, progress, errors }
 */
export const usePreload = (urls, type = 'image') => {
  const [loaded, setLoaded] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const loadPromises = urls.map(url => {
      return new Promise((resolve, reject) => {
        let element;

        switch (type) {
          case 'image':
            element = new Image();
            element.onload = () => resolve(url);
            element.onerror = () => reject(url);
            element.src = url;
            break;
          case 'script':
            element = document.createElement('script');
            element.onload = () => resolve(url);
            element.onerror = () => reject(url);
            element.src = url;
            document.head.appendChild(element);
            break;
          case 'style':
            element = document.createElement('link');
            element.rel = 'stylesheet';
            element.onload = () => resolve(url);
            element.onerror = () => reject(url);
            element.href = url;
            document.head.appendChild(element);
            break;
          default:
            reject(new Error(`Unsupported type: ${type}`));
        }
      });
    });

    Promise.allSettled(loadPromises).then(results => {
      const successful = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      
      const failed = results
        .filter(result => result.status === 'rejected')
        .map(result => result.reason);

      setLoaded(successful);
      setErrors(failed);
    });
  }, [urls, type]);

  const progress = urls.length > 0 ? (loaded.length / urls.length) * 100 : 0;

  return { loaded, progress, errors };
};

/**
 * Hook per intersection observer (lazy loading immagini, animazioni)
 * @param {Object} options - Opzioni per IntersectionObserver
 * @returns {Array} - [ref, isIntersecting, entry]
 */
export const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [entry, setEntry] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        setEntry(entry);
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [options]);

  return [ref, isIntersecting, entry];
};

/**
 * Hook per gestione memoria di componenti pesanti
 * @param {Function} factory - Funzione che crea il valore pesante
 * @param {Array} deps - Dipendenze per ricalcolo
 * @returns {any} - Valore memoizzato
 */
export const useHeavyComputation = (factory, deps) => {
  return useMemo(() => {
    const start = performance.now();
    const result = factory();
    const end = performance.now();
    return result;
  }, deps);
};

/**
 * Hook per batch updates (riduce re-render)
 * @param {number} delay - Ritardo per batch
 * @returns {Function} - Funzione per aggiungere update al batch
 */
export const useBatchUpdates = (delay = 100) => {
  const updatesRef = useRef([]);
  const timeoutRef = useRef(null);

  const batchUpdate = useCallback((updateFn) => {
    updatesRef.current.push(updateFn);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const updates = [...updatesRef.current];
      updatesRef.current = [];
      
      // Applica tutti gli update in un unico render
      updates.forEach(update => update());
    }, delay);
  }, [delay]);

  return batchUpdate;
};

/**
 * Componente per lazy loading di immagini
 */
export const LazyImage = React.memo(({ src, alt, className, ...props }) => {
  const [imageRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '100px'
  });
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setError(true);
  }, []);

  return (
    <div ref={imageRef} className={className} {...props}>
      {isVisible && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            width: '100%',
            height: 'auto'
          }}
        />
      )}
      {!loaded && isVisible && !error && (
        <div style={{ 
          background: '#f0f0f0', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '100px'
        }}>
          Caricamento...
        </div>
      )}
      {error && (
        <div style={{ 
          background: '#ffebee', 
          color: '#c62828',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '100px'
        }}>
          Errore caricamento immagine
        </div>
      )}
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

export default {
  useDebounce,
  useThrottle,
  useLazyData,
  useVirtualList,
  useOptimizedState,
  usePreload,
  useIntersectionObserver,
  useHeavyComputation,
  useBatchUpdates,
  LazyImage
};